import { useParams } from 'react-router-dom'
import { Fragment, useRef, useContext, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { APIContext } from 'context/APIContext'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { updateSetWeightRecommendation, swapSlotExercise, undoSlotExerciseSwap, IsActiveWorkoutSlotSwappable } from 'redux/slices/activeWorkoutV5'
import store from 'redux/store'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ActiveWorkoutV5Header from 'components/ActiveWorkoutV5/ActiveWorkoutV5Header'

import ContentFrame from 'components/Frame/ContentFrame'
import PhaseHeaderV5 from 'components/ActiveWorkoutV5/PhaseHeaderV5'
import RoundDivider from 'components/ActiveWorkout/RoundDivider'
import ExerciseSetListItemV5 from 'components/ActiveWorkoutV5/ExerciseSetListItemV5'

export default function ActiveWorkoutV5() {

    const dispatch = useDispatch()

    const { urlWorkoutId } = useParams()

    const { exerciseHashMap } = useGetExercises()

    const { GetWeightRecommendation } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const loadingRecommendationsRef = useRef(new Set()) // Track in-flight recommendation requests
    const latestRecommendationRequestRef = useRef({}) // requestKey -> id of its latest request (only the latest one lands)
    
    const activeWorkout = useSelector((state) => state.activeWorkoutV5.activeWorkouts[urlWorkoutId])

    const kinesisWorkoutAssignmentType = activeWorkout?.kinesisWorkoutAssignmentType
    const groupId = activeWorkout?.groupDetails?.id ?? null
    const groupIndividuals = activeWorkout?.groupIndividuals ?? []
    const athleteId = activeWorkout?.userId ?? null

    const CalculateWeightRecommendations = (phaseIndexWhitelist = null, slotIndexWhitelist = null, forceReload = false) => {

        // Hacky fix to make sure we are using the latest active workout data that has just been dispatched to the redux store
        const latestActiveWorkout = store.getState().activeWorkoutV5.activeWorkouts[urlWorkoutId]

        if (Object.keys(exerciseHashMap).length === 0) {
            return
        }

        latestActiveWorkout.workout.phases.forEach((phase, phaseIndex) => {

            if (phaseIndexWhitelist !== null && phaseIndexWhitelist.includes(phaseIndex) === false) {
                return
            }

            phase.slots.forEach((slot, slotIndex) => {

                if (slotIndexWhitelist !== null && slotIndexWhitelist.includes(slotIndex) === false) {
                    return
                }

                slot.sets.forEach((set, setIndex) => {

                    const requestKey = `${phaseIndex}-${slotIndex}-${setIndex}`

                    // A forced reload makes any in flight request stale, even when this set is not re-requested (e.g. swapped to a bodyweight exercise)
                    if (forceReload === true) {
                        latestRecommendationRequestRef.current[requestKey] = (latestRecommendationRequestRef.current[requestKey] ?? 0) + 1
                        loadingRecommendationsRef.current.delete(requestKey)
                    }

                    // A forced reload (after a save or swap) re-requests even when a request is in flight: that one is stale
                    if (forceReload === false && loadingRecommendationsRef.current.has(requestKey)) {
                        return
                    }

                    if (forceReload === false && set.weightRecommendationLoaded === true) {
                        return
                    }

                    if (set.skipped === 1 || set.completed === 1) {
                        return
                    }

                    const exerciseId = slot.exerciseId
                    const exercise = exerciseHashMap[exerciseId] ?? null

                    if (exercise === null || exercise.exerciseType === 'bodyweight') {
                        return
                    }
                    
                    const setUnitsAssigned = set.setUnitsAssigned
                    const taper = set.taper
                    const taperDampener = set.taperDampener
                    const localCompletedSets = __CompileLocalCompletedSetsData(slot)

                    __LoadSetWeightRecommendationIntoRedux(
                        requestKey, 
                        urlWorkoutId,
                        kinesisWorkoutAssignmentType, 
                        groupId, 
                        athleteId, 
                        phaseIndex,
                        slotIndex,
                        setIndex,
                        exerciseId, 
                        setUnitsAssigned, 
                        taper, 
                        taperDampener, 
                        localCompletedSets,
                        slot.workoutSlotId ?? null
                    )
                })
            })
        })
    }

    const __CompileLocalCompletedSetsData = (slot) => {
        const localCompletedSets = []
        const exercise = exerciseHashMap[slot.exerciseId] ?? null

        slot.sets.forEach((set, setIndex) => {

            if (set.completed !== 1) {
                return
            }

            localCompletedSets.push({
                exerciseId: slot.exerciseId,
                exerciseName: exercise?.name ?? '',
                currentSet: setIndex + 1,
                setsAssigned: slot.sets.length,
                weightRecommendation: set.weightRecommendation,
                weightRecommendationRounded: set.weightRecommendationRounded,
                formulaTag: set.formulaTag,
                difficulty: set.difficulty ?? 0,
                setUnitsCompleted: set.setUnitsCompleted,
                setUnitsAssigned: set.setUnitsAssigned,
                override: set.override ?? 0,
                taper: set.taper ? 1 : 0,
                taperDampener: set.taperDampener,
                completeDate: set.completedTimestamp
            })
        })

        return localCompletedSets
    }

    const __LoadSetWeightRecommendationIntoRedux = async (
        requestKey,
        workoutId,
        kinesisWorkoutAssignmentType,
        groupId,
        athleteId,
        phaseIndex,
        slotIndex,
        setIndex,
        exerciseId,
        setUnitsAssigned,
        taper,
        taperDampener,
        localCompletedSets,
        workoutSlotId = null
    ) => {

        loadingRecommendationsRef.current.add(requestKey)

        const requestId = (latestRecommendationRequestRef.current[requestKey] ?? 0) + 1
        latestRecommendationRequestRef.current[requestKey] = requestId

        // Reset existing weight recommendation data

        const emptyPayload = {
            workoutId: workoutId,
            kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
            phaseIndex: phaseIndex, 
            slotIndex: slotIndex,
            setIndex: setIndex,
            weightRecommendationData: {
                weightRecommendation: 0,
                weightRecommendationRounded: 0,
                formulaTag: '',
                equipmentDetails: null
            },
            weightRecommendationLoaded: false
        }

        dispatch(updateSetWeightRecommendation(emptyPayload));

        // Calculate new weight recommendation data

        let weightRecommendationData = await GetWeightRecommendation(groupId, athleteId, exerciseId, setUnitsAssigned, taper, taperDampener, localCompletedSets, 1, workoutSlotId)

        // A failed load falls back to a default weight so the set never stays loading (same default as the athlete app)
        if (weightRecommendationData == null) {
            weightRecommendationData = {
                weightRecommendation: 15,
                weightRecommendationRounded: 15,
                formulaTag: 'error-handling-default',
                equipmentDetails: null
            }
        }

        // A newer request for this set (a save or swap since) owns it. The workout can also have been ended or discarded
        if (
            latestRecommendationRequestRef.current[requestKey] !== requestId
            || store.getState().activeWorkoutV5.activeWorkouts[workoutId] === undefined
        ) {
            return
        }

        // Update redux store with new weight recommendation data

        const weightRecommendationDataPayload = {
            workoutId: workoutId,
            kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
            phaseIndex: phaseIndex, 
            slotIndex: slotIndex,
            setIndex: setIndex,
            weightRecommendationData: weightRecommendationData,
            weightRecommendationLoaded: true
        }

        dispatch(updateSetWeightRecommendation(weightRecommendationDataPayload));

        loadingRecommendationsRef.current.delete(requestKey)
        
    }

    // Exercise swaps. Frontend only until the workout is ended, when the swapped slots are saved

    const IsSlotSwappable = (phaseIndex, slotIndex) => {
        return IsActiveWorkoutSlotSwappable(store.getState().activeWorkoutV5.activeWorkouts[urlWorkoutId], phaseIndex, slotIndex)
    }

    const ShowSwapWarningModal = () => {
        const modalData = {
            key: ModalKey.Text,
            fullscreen: false,
            size: 'quarter',
            titleText: 'Cannot swap exercise with saved sets',
            bodyText: [
                'This exercise already has saved sets.',
                'Edit any saved sets back to incomplete before swapping.'
            ]
        }

        ShowModal(modalData)
    }

    const ShowExerciseSwapModal = (phaseIndex, slotIndex) => {
        if (IsSlotSwappable(phaseIndex, slotIndex) === false) {
            ShowSwapWarningModal()
            return
        }

        const modalData = {
            key: ModalKey.ExerciseLibrary,
            size: 'third',
            subHeaderText: 'Select an exercise to swap in',
            OnExerciseSelected: (exercise) => {
                // Checked again: a set could have been saved while the library was open
                if (IsSlotSwappable(phaseIndex, slotIndex) === false) return

                dispatch(swapSlotExercise({ workoutId: urlWorkoutId, phaseIndex, slotIndex, exerciseId: exercise.id }))

                CalculateWeightRecommendations([phaseIndex], [slotIndex], true)
            }
        }

        ShowModal(modalData)
    }

    const UndoExerciseSwap = (phaseIndex, slotIndex) => {
        if (IsSlotSwappable(phaseIndex, slotIndex) === false) {
            ShowSwapWarningModal()
            return
        }

        dispatch(undoSlotExerciseSwap({ workoutId: urlWorkoutId, phaseIndex, slotIndex }))

        CalculateWeightRecommendations([phaseIndex], [slotIndex], true)
    }

    useEffect(() => { CalculateWeightRecommendations() }, [exerciseHashMap])

    if (
        Object.keys(exerciseHashMap).length === 0 
        || !activeWorkout 
        || !activeWorkout.workout
    ) {
        return (
            <ScreenFrame limitWidth={false} alwaysHideResumeWorkoutHeader={true}>
                <div className='space-y-4 p-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame limitWidth={false} alwaysHideResumeWorkoutHeader={true}>
            <ActiveWorkoutV5Header
                kinesisWorkoutAssignmentType={kinesisWorkoutAssignmentType}
                groupId={groupId}
                athleteId={athleteId}
                workoutId={urlWorkoutId}
                activeWorkout={activeWorkout}
            />

            {activeWorkout.workout.phases.map((phase, phaseIndex) => (
                <ContentFrame key={phaseIndex}>
                    <div className="w-full">
                        <PhaseHeaderV5 
                            workoutId={urlWorkoutId} 
                            phaseIndex={phaseIndex} 
                            phase={phase} 
                        />

                        {(() => {
                            var items = []

                            if (phase.type === 'straight') {
                                phase.slots.forEach((slot, slotIndex) => {
                                    slot.sets.forEach((set, setIndex) => {
                                        items.push({
                                            id: `phase-${phaseIndex}-straight-slot-${slotIndex}-set-${setIndex}`,
                                            renderItem: (
                                                <ExerciseSetListItemV5
                                                    exerciseHashMap={exerciseHashMap}
                                                    kinesisWorkoutAssignmentType={kinesisWorkoutAssignmentType}
                                                    groupIndividuals={groupIndividuals}
                                                    phaseIndex={phaseIndex}
                                                    phase={phase}
                                                    slotIndex={slotIndex}
                                                    slot={slot}
                                                    setIndex={setIndex}
                                                    set={set}
                                                    CalculateWeightRecommendations={CalculateWeightRecommendations}
                                                    OnSwapButton={ShowExerciseSwapModal}
                                                    OnUndoSwapButton={UndoExerciseSwap}
                                                />
                                            )
                                        })
                                    })
                                })
                            }

                            if (phase.type === 'circuit') {
                                Array.from({ length: phase.sets }, (_, roundIndex) => {
                                    items.push({
                                        id: `phase-${phaseIndex}-circuit-round-${roundIndex}`,
                                        renderItem: (
                                            <RoundDivider
                                                expanded={phase.expanded}
                                                roundIndex={roundIndex}
                                            />
                                        )
                                    })

                                    phase.slots.forEach((slot, slotIndex) => {
                                        items.push({
                                            id: `phase-${phaseIndex}-circuit-round-${roundIndex}-slot-${slotIndex}`,
                                            renderItem: (
                                                <ExerciseSetListItemV5
                                                    exerciseHashMap={exerciseHashMap}
                                                    kinesisWorkoutAssignmentType={kinesisWorkoutAssignmentType}
                                                    groupIndividuals={groupIndividuals}
                                                    phaseIndex={phaseIndex}
                                                    phase={phase}
                                                    slotIndex={slotIndex}
                                                    slot={slot}
                                                    setIndex={roundIndex}
                                                    set={slot.sets[roundIndex]}
                                                    CalculateWeightRecommendations={CalculateWeightRecommendations}
                                                    OnSwapButton={ShowExerciseSwapModal}
                                                    OnUndoSwapButton={UndoExerciseSwap}
                                                />
                                            )
                                        })
                                    })
                                })
                            }

                            return (
                                <div className='w-full divide-y divide-gray-200 overflow-y-auto'>
                                    {items.map((item) => (
                                        <Fragment key={item.id}>
                                            {item.renderItem}
                                        </Fragment>
                                    ))}
                                </div>
                            )
                        })()}
                    </div>
                </ContentFrame>
            ))}
        </ScreenFrame>
    )
}